import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  bindPreferredDriver,
  clearPendingReferralCode,
  getPendingReferralCode,
  resolveReferralDriver,
  ReferralDriver,
} from "../../services/referral";
import { RID, TOKEN } from "../../constants/localStoreKeys";
import StoreButtons from "./StoreButtons";
import { styles } from "./styles";
import "../../containers/MobileLanding/index.scss";

type State = "binding" | "done" | "failed" | "nothing-pending";

/**
 * The end of the QR funnel: the passenger now has an account, so the stored
 * code is redeemed and the driver bound.
 *
 * This is the only place the bind happens. Signup and login are shared with
 * every other entry point and must not grow referral logic, so the flow parks
 * the code and comes here once a session exists.
 */
const ReferralComplete: React.FC = () => {
  const history = useHistory();
  const [state, setState] = useState<State>("binding");
  const [driver, setDriver] = useState<ReferralDriver | null>(null);
  const [error, setError] = useState<string | undefined>();
  // Binding is not idempotent-free: React 18 double-invokes effects in dev, and
  // a second bind would be a wasted round trip on a code we already consumed.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = getPendingReferralCode();
    const userId = localStorage.getItem(RID);

    if (!code) {
      setState("nothing-pending");
      return;
    }
    if (!localStorage.getItem(TOKEN) || !userId) {
      // Landed here without a session — send them back to sign up. The code
      // stays stored, so the loop closes when they return.
      history.replace("/auth/register");
      return;
    }

    resolveReferralDriver(code).then(setDriver);

    bindPreferredDriver({ userId, code }).then((result) => {
      if (result.ok) {
        // Cleared only on success: a network failure should leave the code in
        // place so a retry or a later visit can still pair them.
        clearPendingReferralCode();
        setState("done");
      } else {
        setError(result.error);
        setState("failed");
      }
    });
  }, [history]);

  const driverName = driver?.name?.trim();

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {state === "binding" && (
          <>
            <h1 style={styles.title}>Setting up…</h1>
            <p style={styles.spinner}>Adding your preferred driver.</p>
          </>
        )}

        {state === "done" && (
          <>
            <h1 style={styles.title}>You’re all set</h1>
            <p style={styles.body}>
              {driverName
                ? `${driverName} is now your preferred driver. Your ride requests go to them first — and if they can’t take it, we’ll find you someone else automatically.`
                : "Your preferred driver is saved. Your ride requests go to them first — and if they can’t take it, we’ll find you someone else automatically."}
            </p>
          </>
        )}

        {state === "failed" && (
          <>
            <h1 style={styles.title}>Couldn’t save your driver</h1>
            <p style={styles.error}>{error}</p>
            <p style={styles.body}>
              Your account is ready. You can add your driver later from
              Preferred Driver in the app.
            </p>
          </>
        )}

        {state === "nothing-pending" && (
          <>
            <h1 style={styles.title}>Nothing to pair</h1>
            <p style={styles.body}>
              Scan your driver’s QR code to set them as your preferred driver.
            </p>
          </>
        )}

        {state !== "binding" && (
          <>
            {/* The download prompt lives HERE, at the end of the funnel — not
                in front of it. A passenger who has just scanned a driver's QR
                needs to see who they are pairing with first; asking them to
                install something before that loses them. By this point the
                pairing is already saved, so the app is an upgrade rather than
                a toll gate. */}
            <p style={styles.body}>
              Get the SafeRides app to request rides — it’s the only place you can book.
            </p>
            <StoreButtons variant="saferides" />
            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={() => history.replace("/dashboard")}
            >
              Continue in browser
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ReferralComplete;
